import React, {useState, useRef, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Button, Box, Stack, Text, useMultiStyleConfig, FormControl, FormLabel} from '@chakra-ui/react'
import WebDrawer from '../../../../components/web-drawer'
import AddressDisplay from './address-display'
import {ChevronDownType2, CheckMarkIcon} from '../../../../components/icons'
import {useBrand} from '../../../../contexts/brand-context'
import {expandAddressSuggestions} from '../../../../utils/address-utils'

const UNITS_PER_GROUP = 60

const GeoMismatchDrawer = ({
    isOpen,
    onClose,
    enteredAddress,
    suggestions,
    selectedAddress,
    setSelectedAddress
}) => {
    const styles = useMultiStyleConfig('AddressValidationDrawer', {variant: 'geoMismatch'})
    const nestedStyles = useMultiStyleConfig('AddressValidationDrawer', {variant: 'geoMismatchNested'})
    const selectStyles = useMultiStyleConfig('WebFloatingSelect', {})
    const intl = useIntl()

    const [isSelectOpen, setIsSelectOpen] = useState(false)
    const [isNestedOpen, setIsNestedOpen] = useState(false)
    const [expandedGroupKey, setExpandedGroupKey] = useState(null)
    const [triggerLabel, setTriggerLabel] = useState('')
    const [selectedSuggestion, setSelectedSuggestion] = useState(null)
    const containerRef = useRef(null)
    const dropdownRef = useRef(null)
    const nestedBodyRef = useRef(null)
    const groupHeaderRefs = useRef({})
    const {isGarage} = useBrand()

    // Scroll the expanded group header into view inside the nested drawer's DrawerBody
    useEffect(() => {
        if (!isNestedOpen || !expandedGroupKey) return

        const attemptScroll = () => {
            const target = groupHeaderRefs.current[expandedGroupKey]
            if (!target) return

            // Walk up to find the scrollable DrawerBody container
            let scrollContainer = target.parentElement
            while (scrollContainer) {
                const {overflow, overflowY} = window.getComputedStyle(scrollContainer)
                if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
                    break
                }
                scrollContainer = scrollContainer.parentElement
            }

            if (scrollContainer) {
                const containerRect = scrollContainer.getBoundingClientRect()
                const targetRect = target.getBoundingClientRect()
                const offset = targetRect.top - containerRect.top + scrollContainer.scrollTop
                scrollContainer.scrollTo({top: offset, behavior: 'smooth'})
            }
        }

        const timer = setTimeout(attemptScroll, 0)
        return () => clearTimeout(timer)
    }, [isNestedOpen, expandedGroupKey])

    useEffect(() => {
        if (!isSelectOpen) return
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsSelectOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [isSelectOpen])

    const handleDropdownWheel = (e) => {
        e.preventDefault()
        dropdownRef.current.scrollTop += e.deltaY
    }

    const handleNestedDrawerWheel = (e) => {
        // For nested drawer, we need to find the actual scrollable container (DrawerBody)
        // Walk up from the event target to find the scrollable parent
        let scrollContainer = e.target
        while (scrollContainer) {
            const {overflow, overflowY} = window.getComputedStyle(scrollContainer)
            if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
                e.preventDefault()
                scrollContainer.scrollTop += e.deltaY
                return
            }
            scrollContainer = scrollContainer.parentElement
        }
    }

    const groupedOptions = useMemo(() => {
        if (!suggestions || suggestions.length === 0) return []

        // Expand comma-separated apartments into individual suggestions
        const expandedSuggestions = expandAddressSuggestions(suggestions)

        const result = []
        const groups = {}
        const flatItems = []

        expandedSuggestions.forEach((s) => {
            if (!s.address2) {
                flatItems.push(s)
            } else {
                const key = `${s.address1}|${s.city}|${s.postalCode}`
                if (!groups[key]) {
                    groups[key] = {
                        address1: s.address1,
                        city: s.city,
                        stateCode: s.stateCode,
                        postalCode: s.postalCode,
                        items: []
                    }
                }
                groups[key].items.push(s)
            }
        })

        // Flat suggestions (no address2)
        flatItems.forEach((s) => {
            result.push({
                type: 'single',
                displayLabel: [s.address1, s.city, s.stateCode, s.postalCode].filter(Boolean).join(', '),
                fullValue: [s.address1, s.city, s.stateCode, s.postalCode].filter(Boolean).join(', '),
                suggestion: s
            })
        })

        // Grouped suggestions (with address2)
        Object.values(groups).forEach((group) => {
            const sortedItems = [...group.items].sort((a, b) =>
                (a.address2 || '').localeCompare(b.address2 || '', undefined, {
                    numeric: true,
                    sensitivity: 'base'
                })
            )

            if (sortedItems.length === 1) {
                const s = sortedItems[0]
                result.push({
                    type: 'single',
                    displayLabel: [s.address1, s.city, s.stateCode, s.postalCode].filter(Boolean).join(', '),
                    fullValue: [s.address1, s.address2, s.city, s.stateCode, s.postalCode]
                        .filter(Boolean)
                        .join(', '),
                    suggestion: s
                })
            } else {
                for (let i = 0; i < sortedItems.length; i += UNITS_PER_GROUP) {
                    const chunk = sortedItems.slice(i, i + UNITS_PER_GROUP)
                    const start = i + 1
                    const end = i + chunk.length
                    result.push({
                        type: 'group',
                        key: `${group.address1}|${start}-${end}`,
                        baseLabel: [group.address1, group.city, group.stateCode, group.postalCode]
                            .filter(Boolean)
                            .join(', '),
                        rangeLabel: `${start}-${end}`,
                        items: chunk
                    })
                }
            }
        })

        return result
    }, [suggestions])

    const handleFlatSelect = (opt) => {
        // Include address2 in the display if it exists
        const label = opt.suggestion?.address2 
            ? `${opt.displayLabel}, ${opt.suggestion.address2}`
            : opt.displayLabel
        setTriggerLabel(label)
        setSelectedAddress(opt.fullValue)
        setSelectedSuggestion(opt.suggestion)
        setIsSelectOpen(false)
    }

    const handleGroupClick = (opt) => {
        setExpandedGroupKey(opt.key)
        setIsSelectOpen(false)
        setIsNestedOpen(true)
    }

    const handleUnitSelect = (unit) => {
        const displayLbl = [unit.address1, unit.city, unit.stateCode, unit.postalCode]
            .filter(Boolean)
            .join(', ')
        // Add apartment number to display label
        const displayLblWithUnit = unit.address2
            ? `${displayLbl}, ${unit.address2}`
            : displayLbl
        const fullVal = [unit.address1, unit.address2, unit.city, unit.stateCode, unit.postalCode]
            .filter(Boolean)
            .join(', ')
        setTriggerLabel(displayLblWithUnit)
        setSelectedAddress(fullVal)
        setSelectedSuggestion(unit)
        setIsNestedOpen(false)
    }

    const isFloated = isSelectOpen || !!triggerLabel

    const placeholderLabel = intl.formatMessage(
        {
            defaultMessage: 'Choose your address ({count} results)',
            id: 'shipping_address.modal.choose_address'
        },
        {count: suggestions.length}
    )

    return (
        <WebDrawer
            noBodyStyle={true}
            isOpen={isOpen}
            onClose={() => onClose(false)}
            size="hug"
            headerInContent
            footer={
                <Button
                    variant="webPrimary"
                    w={'100%'}
                    onClick={() => onClose(true, {suggestion: selectedSuggestion})}
                    isDisabled={!selectedAddress}
                >
                    <FormattedMessage
                        defaultMessage="Use selected address"
                        id="shipping_address.modal.use_selected_address"
                    />
                </Button>
            }
        >
            <Box {...styles.body}>
                <Text {...styles.title} mb={3}>
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="Confirm your address"
                            id="shipping_address.modal.confirm_address.garage"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Confirm your address"
                            id="shipping_address.modal.confirm_address.dynamite"
                        />
                    )}
                </Text>
                <Text {...styles.description} mb={5}>
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="To ensure accurate and timely delivery, please select the one that best corresponds to your given address."
                            id="shipping_address.modal.ensure_delivery.garage"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="To ensure accurate and timely delivery, please select the one that best corresponds to your given address."
                            id="shipping_address.modal.ensure_delivery.dynamite"
                        />
                    )}
                </Text>
                <Text {...styles.label} mb={3}>
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="You Entered"
                            id="shipping_address.modal.entered_address.garage"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="You Entered"
                            id="shipping_address.modal.entered_address.dynamite"
                        />
                    )}
                </Text>
                <Box {...styles.addressDisplay} mb={5}>
                    <AddressDisplay address={enteredAddress} />
                </Box>
                <Text {...styles.label} mb={3}>
                    <FormattedMessage
                        defaultMessage="Suggested Address"
                        id="shipping_address.modal.suggested_address"
                    />
                </Text>

                {/* Custom grouped select */}
                <Box position="relative" ref={containerRef}>
                    <FormControl>
                        <FormLabel
                            sx={{
                                ...selectStyles.label,
                                ...(isFloated ? selectStyles.labelFloated : {})
                            }}
                        >
                            {placeholderLabel}
                            <span
                                className="web-field-suffix"
                                style={{color: 'var(--chakra-colors-webTypo-textPrimary)'}}
                            >
                                {' '}
                                *
                            </span>
                        </FormLabel>
                        <Box
                            as="button"
                            type="button"
                            __css={selectStyles.trigger}
                            onClick={() => setIsSelectOpen((p) => !p)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') setIsSelectOpen(false)
                            }}
                            aria-expanded={isSelectOpen}
                            aria-haspopup="listbox"
                        >
                            <Text as="span" sx={selectStyles.triggerText}>
                                {triggerLabel || ''}
                            </Text>
                            <ChevronDownType2
                                sx={{
                                    ...selectStyles.chevron,
                                    transform: isSelectOpen
                                        ? 'translateY(-50%) rotate(180deg)'
                                        : 'translateY(-50%) rotate(0deg)',
                                    transition: 'transform 0.2s ease-in-out'
                                }}
                            />
                        </Box>

                        {isSelectOpen && (
                            <Box role="listbox" __css={selectStyles.dropdown} ref={dropdownRef} onWheel={handleDropdownWheel}>
                                {groupedOptions.map((opt) => {
                                    if (opt.type === 'single') {
                                        const isSelected = selectedAddress === opt.fullValue
                                        return (
                                            <Box
                                                key={opt.fullValue}
                                                role="option"
                                                aria-selected={isSelected}
                                                __css={selectStyles.option}
                                                onClick={() => handleFlatSelect(opt)}
                                            >
                                                <Text sx={selectStyles.optionText}>
                                                    {opt.displayLabel}
                                                </Text>
                                                {isSelected && (
                                                    <CheckMarkIcon sx={selectStyles.checkIcon} />
                                                )}
                                            </Box>
                                        )
                                    }
                                    // Group option
                                    return (
                                        <Box
                                            key={opt.key}
                                            role="option"
                                            __css={selectStyles.option}
                                            onClick={() => handleGroupClick(opt)}
                                        >
                                            <Box flex="1">
                                                <Text
                                                    sx={{
                                                        ...selectStyles.optionText,
                                                        fontWeight: 'medium'
                                                    }}
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Show results for {range}"
                                                        id="address.suggestions.show_results"
                                                        values={{range: opt.rangeLabel}}
                                                    />
                                                </Text>
                                                <Text
                                                    sx={{
                                                        ...selectStyles.optionText,
                                                        color: 'webTypo.textSecondary'
                                                    }}
                                                >
                                                    {opt.baseLabel}
                                                </Text>
                                            </Box>
                                            <ChevronDownType2
                                                sx={{
                                                    w: '14px',
                                                    h: '14px',
                                                    transform: 'rotate(-90deg)',
                                                    flexShrink: 0
                                                }}
                                            />
                                        </Box>
                                    )
                                })}
                            </Box>
                        )}
                    </FormControl>
                </Box>
                <Button
                    variant="unstyled"
                    w={'100%'}
                    {...styles.link}
                    onClick={() => onClose(false)}
                    mt={5}
                >
                    <Text as="span" {...styles.link}>
                        <FormattedMessage
                            defaultMessage="Modify my address"
                            id="shipping_address.modal.modify_address"
                        />
                    </Text>
                </Button>
            </Box>

            {/* Nested drawer — all suggestions, clicked group auto-expanded */}
            <WebDrawer
                isOpen={isNestedOpen}
                onClose={() => setIsNestedOpen(false)}
                size="85vh"
                id="geo-mismatch-units"
                noBodyStyle={true}
            >
                <Box {...nestedStyles.body} ref={nestedBodyRef} onWheel={handleNestedDrawerWheel}>
                    {groupedOptions.map((opt) => {
                        if (opt.type === 'single') {
                            const isSelected = selectedAddress === opt.fullValue
                            return (
                                <Box
                                    key={opt.fullValue}
                                    role="option"
                                    aria-selected={isSelected}
                                    __css={selectStyles.option}
                                    onClick={() =>
                                        handleUnitSelect({
                                            address1: opt.suggestion.address1,
                                            address2: opt.suggestion.address2,
                                            city: opt.suggestion.city,
                                            stateCode: opt.suggestion.stateCode,
                                            postalCode: opt.suggestion.postalCode
                                        })
                                    }
                                >
                                    <Text sx={selectStyles.optionText}>{opt.displayLabel}</Text>
                                    {isSelected && <CheckMarkIcon sx={selectStyles.checkIcon} />}
                                </Box>
                            )
                        }

                        // Group option — expanded if it matches the clicked group
                        const isExpanded = expandedGroupKey === opt.key
                        return (
                            <Box key={opt.key}>
                                <Box
                                    ref={(el) => {
                                        groupHeaderRefs.current[opt.key] = el
                                    }}
                                    role="option"
                                    __css={{
                                        ...selectStyles.option,
                                        ...(isExpanded ? {bg: 'webTheme.fieldHover'} : {})
                                    }}
                                    onClick={() => {
                                        const next = isExpanded ? null : opt.key
                                        setExpandedGroupKey(next)
                                    }}
                                >
                                    <Box flex="1">
                                        <Text
                                            sx={{...selectStyles.optionText, fontWeight: 'medium'}}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Show results for {range}"
                                                id="address.suggestions.show_results"
                                                values={{range: opt.rangeLabel}}
                                            />
                                        </Text>
                                        <Text
                                            sx={{
                                                ...selectStyles.optionText,
                                                color: 'webTypo.textSecondary'
                                            }}
                                        >
                                            {opt.baseLabel}
                                        </Text>
                                    </Box>
                                    <ChevronDownType2
                                        sx={{
                                            w: '14px',
                                            h: '14px',
                                            transform: isExpanded
                                                ? 'rotate(180deg)'
                                                : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease-in-out',
                                            flexShrink: 0
                                        }}
                                    />
                                </Box>
                                {isExpanded &&
                                    opt.items.map((unit, unitIdx) => {
                                        const fullVal = [
                                            unit.address1,
                                            unit.address2,
                                            unit.city,
                                            unit.stateCode,
                                            unit.postalCode
                                        ]
                                            .filter(Boolean)
                                            .join(', ')
                                        const isSelected = selectedAddress === fullVal
                                        // Scroll target: the selected unit, or the first unit if none selected
                                        return (
                                            <Box
                                                key={`${unit.address2}-${unit.address1}`}
                                                __css={{
                                                    ...selectStyles.option,
                                                    pl: 6,
                                                    bg: isSelected
                                                        ? 'webTheme.backgroundPrimaryBrand'
                                                        : 'white'
                                                }}
                                                onClick={() => handleUnitSelect(unit)}
                                            >
                                                <Text sx={selectStyles.optionText}>
                                                    {unit.address2}
                                                </Text>
                                                {isSelected && (
                                                    <CheckMarkIcon sx={selectStyles.checkIcon} />
                                                )}
                                            </Box>
                                        )
                                    })}
                            </Box>
                        )
                    })}
                </Box>
            </WebDrawer>
        </WebDrawer>
    )
}

GeoMismatchDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    enteredAddress: PropTypes.object.isRequired,
    suggestions: PropTypes.array.isRequired,
    selectedAddress: PropTypes.string,
    setSelectedAddress: PropTypes.func.isRequired
}

export default GeoMismatchDrawer
